import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Alert, Spinner } from 'react-bootstrap';
import axios from 'axios';

const MappingManagementPage = () => {
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

  useEffect(() => {
    fetchMappings();
  }, []);

  const fetchMappings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${apiUrl}/admin/mappings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMappings(response.data.mappings);
    } catch (err) {
      setError('Failed to load mappings');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!importFile) return;
    
    setImporting(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', importFile);
      
      await axios.post(`${apiUrl}/admin/mappings/import`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setShowImportModal(false);
      setImportFile(null);
      fetchMappings();
    } catch (err) {
      setError('Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${apiUrl}/admin/mappings/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'column_mappings.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      setError('Export failed');
    }
  };
  

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Column Mappings ({mappings.length})</h2>
        <div>
            <Button variant="outline-primary" className="me-2" onClick={handleExport}>
            Export CSV
            </Button>
            <Button variant="primary" onClick={() => setShowImportModal(true)}>
            Import Mappings
            </Button>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center p-4">
              <Spinner animation="border" />
            </div>
          ) : (
            <Table responsive hover size="sm">
              <thead>
                <tr>
                  <th>CSV Column</th>
                  <th>Display Name</th>
                  <th>Target Table</th>
                  <th>Target Column</th>
                  <th>Data Type</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {mappings.map(mapping => (
                  <tr key={mapping.id}>
                    <td><code>{mapping.csv_column_name}</code></td>
                    <td>{mapping.display_name}</td>
                    <td><span className="badge bg-info">{mapping.target_table}</span></td>
                    <td>{mapping.target_column}</td>
                    <td>{mapping.data_type}</td>
                    <td>
                      <span className={`badge bg-${mapping.is_active ? 'success' : 'secondary'}`}>
                        {mapping.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Import Modal */}
      <Modal show={showImportModal} onHide={() => setShowImportModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Import Column Mappings</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>Select CSV File</Form.Label>
              <Form.Control
                type="file"
                accept=".csv"
                onChange={(e) => setImportFile(e.target.files[0])}
              />
              <Form.Text className="text-muted">
                CSV must have columns: csv_column_name, display_name, target_table, target_column, data_type
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowImportModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleImport}
            disabled={!importFile || importing}
          >
            {importing ? 'Importing...' : 'Import'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MappingManagementPage;
